import { EditorState, EditorStateConfig, Extension } from "@codemirror/state"
import { EditorView, ViewUpdate, keymap, placeholder as placeholderExtension } from "@codemirror/view"
import { useLayoutEffect, useRef, useCallback, useEffect, useMemo } from "react"
import { html } from "@codemirror/lang-html"
import { javascript, javascriptLanguage, scopeCompletionSource } from "@codemirror/lang-javascript"
import { defaultKeymap, historyKeymap } from "@codemirror/commands"
import { 
  completionKeymap,
  autocompletion
} from "@codemirror/autocomplete"
import { 
  defaultHighlightStyle, 
  syntaxHighlighting, 
  foldKeymap
} from "@codemirror/language"
import { lintKeymap } from "@codemirror/lint"
import { searchKeymap } from "@codemirror/search"
import { EditorView as EditorViewTheme } from "@codemirror/view"
import { useBaseExtensions } from "./use-base-extensions"
import { createFormatExtension, jsEditorKeymap, keyboardHandler } from "../extensions"

interface UseJsEditorProps {
  autoFocus?: boolean
  /**
   * 延迟触发间隔（毫秒），与 onDebouncedChange 搭配使用。
   * 默认 300ms。
   */
  debouncedDelayMs?: number,
  lineWrapping?: boolean,
  onChange?: (value: string, viewUpdate: ViewUpdate) => void,
  onDebouncedChange?: (value: string, viewUpdate: ViewUpdate) => void,
  onEndChange?: (value: string) => void
  onFormat?: (formattedCode: string) => void
  /**
   * Whether to capture Tab key inside the editor.
   * - true: Tab is handled by the editor (indent / completion)
   * - false: Tab moves focus to the next focusable element
   */
  captureTabKey?: boolean
  placeholder?: string
  readonly?: boolean
  value?: string
}

export function useJsEditor(props: UseJsEditorProps) {
  const {
    value,
    autoFocus,
    onChange,
    onDebouncedChange,
    debouncedDelayMs,
    onEndChange,
    readonly,
    lineWrapping,
    placeholder,
    onFormat,
    captureTabKey = true,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const isInternalChangeRef = useRef(false)
  // 用于延迟回调的定时器
  const debouncedTimerRef = useRef<number | null>(null)
  // 记录上一次触发 onEndChange 时的内容，用于避免重复触发
  const lastEndChangeValueRef = useRef<string | null>(value ?? null)

  const customTheme = useMemo(() => {
    return EditorViewTheme.theme({
      "&": {
        height: "100%",
        fontSize: "14px",
        backgroundColor: "transparent",
      },
      ".cm-scroller": {
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        overflow: "auto",
      },
      ".cm-content": {
        caretColor: "var(--foreground-default, #000)",
        minHeight: "100%",
        whiteSpace: "pre",
      },
      "&.cm-focused": {
        outline: "none",
      },
      "&.cm-focused .cm-cursor": {
        borderLeftColor: "var(--foreground-default, #000)",
      },
      "&.cm-focused .cm-selectionBackground, ::selection": {
        backgroundColor: "var(--background-selected, #d4d4d4)",
      },
      ".cm-gutters": {
        color: "var(--foreground-subtle, #999)",
        border: "none",
        paddingRight: "0.5rem",
      },
      ".cm-activeLine": {
        backgroundColor: "transparent",
      },
      ".cm-line": {
        padding: "0",
      },
    })
  }, [])

  const handleEndChange = useCallback(
    (content: string) => {
      // 只有在内容相对于上一次触发 onEndChange 有变化时才调用外部回调
      if (content === lastEndChangeValueRef.current) {
        return
      }

      lastEndChangeValueRef.current = content
      onEndChange?.(content)
    },
    [onEndChange],
  )

  const baseExtensions = useBaseExtensions({
    readonly,
    lineWrapping,
    onChange: (newValue, update) => {
      isInternalChangeRef.current = true
      onChange?.(newValue, update)

      if (onDebouncedChange) {
        if (debouncedTimerRef.current !== null) {
          window.clearTimeout(debouncedTimerRef.current)
        }

        const delay = debouncedDelayMs ?? 300
        debouncedTimerRef.current = window.setTimeout(() => {
          onDebouncedChange(newValue, update)
        }, delay)
      }
    },
    onEndChange: onEndChange ? handleEndChange : undefined,
    theme: customTheme,
    includeLineNumbers: true,
    includeKeymap: false,
  })

  // 格式化扩展
  // 即使没有 onFormat 回调，也需要添加格式化扩展以支持格式化功能
  const formatExtension = useMemo(() => {
    return createFormatExtension(onFormat ?? (() => {}))
  }, [onFormat])

  // JavaScript/HTML 特定的扩展
  const languageExtensions = useMemo((): Extension[] => {
    const keymaps = !readonly
      ? (captureTabKey
        ? jsEditorKeymap
        // When not capturing Tab, remove Tab bindings so that focus can move naturally
        : jsEditorKeymap.filter((binding) => binding.key !== "Tab" && binding.key !== "Shift-Tab"))
      : [
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
        ...lintKeymap,
      ]

    const extensions: Extension[] = [
      html({
        autoCloseTags: true,
      }),
      javascript({
        jsx: false,
        typescript: false,
      }),
      autocompletion({
        activateOnTyping: true,
        icons: true,
      }),
      javascriptLanguage.data.of({
        autocomplete: scopeCompletionSource(globalThis),
      }),
      
      syntaxHighlighting(defaultHighlightStyle),
      keyboardHandler,
      // 添加格式化扩展（即使没有 onFormat 回调也添加，以支持格式化功能）
      ...formatExtension,
      keymap.of([
        // 使用 jsEditorKeymap，包含 HTML/JavaScript 格式化功能
        ...keymaps,
      ]),
    ]

    // Placeholder
    if (placeholder) {
      extensions.push(placeholderExtension(placeholder))
    }

    return extensions
  }, [placeholder, formatExtension, readonly, captureTabKey])

  const createEditor = useCallback(() => {
    if (!containerRef.current) return

    const config: EditorStateConfig = {
      doc: value ?? "",
      extensions: [
        ...baseExtensions,
        ...languageExtensions,
      ],
    }

    const state = EditorState.create(config)

    const editorView = new EditorView({
      parent: containerRef.current,
      state,
    })

    editorViewRef.current = editorView
  }, [value, baseExtensions, languageExtensions])

  // 初始化编辑器
  useLayoutEffect(() => {
    createEditor()

    return () => {
      if (debouncedTimerRef.current !== null) {
        window.clearTimeout(debouncedTimerRef.current)
      }

      if (editorViewRef.current) {
        editorViewRef.current.destroy()
        editorViewRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 当外部 value 改变时，更新编辑器内容
  useEffect(() => {
    const editor = editorViewRef.current
    if (!editor) return

    // 如果这是内部变化触发的更新，跳过
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false
      return
    }

    const currentValue = editor.state.doc.toString()
    const newValue = value ?? ""

    if (currentValue !== newValue) {
      editor.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: newValue,
        },
      })
    }

    // 同步外部受控值到 onEndChange 的比较基线，
    // 避免在内容实际上没有变化时误触发 onEndChange
    lastEndChangeValueRef.current = newValue
  }, [value])

  useEffect(() => {
    if (autoFocus && editorViewRef.current) {
      editorViewRef.current.focus()
    }
  }, [autoFocus])

  return {
    containerRef,
  }
}

